import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Card, Spinner } from '@structural-climbing/ui'
import { type Discipline } from '@structural-climbing/validation'
import { RouteRow } from './RouteRow'
import { AddRouteModal } from './AddRouteModal'
import { EditRouteModal } from './EditRouteModal'
import { RouteDetailModal } from './RouteDetailModal'
import { RouteCommentsModal } from './RouteCommentsModal'
import { SectionsTab } from './SectionsTab'
import type { ClimbingRoute, GymSection } from './types'
import { PageHeader } from '../../components/PageHeader'
import { PageTabs } from '../../components/PageTabs'

type Tab = 'active' | 'archived' | 'sections'

interface RoutesGridProps {
  routes: ClimbingRoute[]
  loading: boolean
  tab: Tab
  onTabChange: (tab: Tab) => void
  onRefresh: () => void
  /** A route moved to the other tab (archived or restored); drop it in place, no reload. */
  onRouteRemoved: (routeId: string) => void
  apiUrl: string
  /** Active discipline tab; scopes the section filter, the Sections tab, and new sections. */
  discipline?: Discipline
  sections: GymSection[]
  /** Sections for the active discipline tab — used for the section filter and Sections tab. */
  filterSections?: GymSection[]
  onSectionsChange: () => void
  selectedSectionId: string | null
  onSectionFilter: (sectionId: string | null) => void
  /** Shown between the page header and the tabs (the discipline switch). */
  aboveTabs?: ReactNode
  /** Shown on the Active tab, above the list. */
  charts?: ReactNode
}

function getLocalDateKey(isoString: string): string {
  const d = new Date(isoString)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function groupRoutesByDate(routes: ClimbingRoute[]): [string, ClimbingRoute[]][] {
  const map = new Map<string, ClimbingRoute[]>()
  for (const route of routes) {
    const key = getLocalDateKey(route.created_at)
    const group = map.get(key)
    if (group) {
      group.push(route)
    } else {
      map.set(key, [route])
    }
  }
  return Array.from(map.entries())
}

export function RoutesGrid({
  routes,
  loading,
  tab,
  onTabChange,
  onRefresh,
  onRouteRemoved,
  apiUrl,
  discipline = 'boulder',
  sections,
  filterSections = sections,
  onSectionsChange,
  selectedSectionId,
  onSectionFilter,
  aboveTabs,
  charts,
}: RoutesGridProps) {
  // Section-level discipline to create new sections with, keyed to the active tab.
  const newSectionDiscipline = discipline === 'lead' ? 'rope' : 'bouldering'
  const { t, i18n } = useTranslation()
  const { getAccessTokenSilently } = useAuth()

  const [addOpen, setAddOpen] = useState(false)
  const [detailRoute, setDetailRoute] = useState<ClimbingRoute | null>(null)
  const [editRoute, setEditRoute] = useState<ClimbingRoute | null>(null)
  const [commentsRoute, setCommentsRoute] = useState<ClimbingRoute | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)

  const fmt = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  async function handleToggleStatus(route: ClimbingRoute) {
    setPendingId(route.id)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/climbing-routes/${route.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: route.status === 'active' ? 'archived' : 'active' }),
      })
      // Remove just this row rather than reloading the list, which would empty
      // it, drop the pages loaded by scrolling, and send the user to the top.
      if (res.ok) onRouteRemoved(route.id)
    } finally {
      setPendingId(null)
    }
  }

  const groups = groupRoutesByDate(routes)

  const tabLabels: Record<Tab, string> = {
    active: t('routes.tabActive'),
    archived: t('routes.tabArchived'),
    sections: t('routes.tabSections'),
  }

  return (
    <>
      <PageHeader
        title={t('routes.title')}
        primaryAction={
          tab === 'active'
            ? { label: t('routes.addRoute'), onPress: () => setAddOpen(true) }
            : undefined
        }
      />
      {aboveTabs}
      <PageTabs
        ariaLabel={t('routes.title')}
        selectedKey={tab}
        onSelectionChange={onTabChange}
        className=""
        items={(['active', 'archived', 'sections'] as const).map((key) => ({
          key,
          label: tabLabels[key],
        }))}
      />

      {tab !== 'sections' && filterSections.length > 0 && (
        <div
          role="group"
          aria-label={t('routes.filterBySection')}
          className="-mx-1 flex gap-1.5 overflow-x-auto px-1 [scrollbar-width:none] md:flex-wrap"
        >
          {[{ id: null, name: t('routes.allSections') }, ...filterSections].map((s) => {
            const isActive = selectedSectionId === s.id
            return (
              <Button
                key={s.id ?? 'all'}
                size="sm"
                radius="full"
                variant={isActive ? 'solid' : 'flat'}
                color={isActive ? 'primary' : 'default'}
                className="shrink-0 font-semibold"
                aria-pressed={isActive}
                onClick={() => onSectionFilter(s.id)}
              >
                {s.name}
              </Button>
            )
          })}
        </div>
      )}

      {tab === 'active' && charts}

      {tab === 'sections' ? (
        <SectionsTab
          sections={filterSections}
          newSectionDiscipline={newSectionDiscipline}
          onRefresh={onSectionsChange}
          apiUrl={apiUrl}
        />
      ) : loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="sm" color="primary" />
        </div>
      ) : routes.length === 0 ? (
        <p className="py-12 text-center text-sm text-foreground/40">
          {tab === 'active' ? t('routes.noRoutes') : t('routes.noArchivedRoutes')}
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map(([dateKey, groupRoutes]) => (
            <div key={dateKey} className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                {fmt.format(new Date(`${dateKey}T12:00:00`))}
              </p>
              <Card shadow="sm" className="divide-y divide-divider">
                {groupRoutes.map((route) => (
                  <RouteRow
                    key={route.id}
                    route={route}
                    apiUrl={apiUrl}
                    onToggleStatus={handleToggleStatus}
                    onViewDetail={setDetailRoute}
                    onViewComments={setCommentsRoute}
                    isPending={pendingId === route.id}
                  />
                ))}
              </Card>
            </div>
          ))}
        </div>
      )}

      <AddRouteModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={onRefresh}
        apiUrl={apiUrl}
        sections={filterSections}
        discipline={discipline}
      />

      <EditRouteModal
        route={editRoute}
        onClose={() => setEditRoute(null)}
        onSaved={() => {
          onRefresh()
          setEditRoute(null)
        }}
        apiUrl={apiUrl}
        sections={filterSections}
        discipline={discipline}
      />

      <RouteDetailModal
        route={detailRoute}
        apiUrl={apiUrl}
        onClose={() => setDetailRoute(null)}
        onEdit={(r) => {
          setDetailRoute(null)
          setEditRoute(r)
        }}
        onViewComments={(r) => {
          setDetailRoute(null)
          setCommentsRoute(r)
        }}
      />

      <RouteCommentsModal
        route={commentsRoute}
        apiUrl={apiUrl}
        onClose={() => setCommentsRoute(null)}
      />
    </>
  )
}
